import { useSearchParams } from 'react-router-dom'
import { useStats } from '../contexts/StatsContext'
import { Nav } from '../components/Nav'
import Footer from '../components/Footer'
import SponsorPanel from '../components/SponsorPanel'
import ClaimGasFlow from '../components/ClaimGasFlow'
import SponsorTable from '../components/SponsorTable'
import { ToastContainer } from '../components/Toast'
import { useToast } from '../hooks/useToast'

type TabType = 'bridge' | 'plsx'

function isValidTab(value: string | null): value is TabType {
  return value === 'bridge' || value === 'plsx'
}

function commas(n: number | bigint): string {
  return n.toLocaleString()
}

const BRIDGE_CRITERIA = [
  'No prior bridge',
  '$10+ USD bridged',
  '< 3,690 PLS balance',
  'No PulseChain txns since fork',
]

const PLSX_CRITERIA = [
  'Sacrificed for PulseX',
  'No large PLS sacrifice (> 1 ETH)',
  'No prior bridge',
  '< 3,690 PLS balance',
]

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab')
  const tab: TabType = isValidTab(rawTab) ? rawTab : 'bridge'
  const { stats, loading, costPerUserUsd, remainingBridge, remainingPlsx } = useStats()
  const { toasts, addToast: _addToast, removeToast } = useToast()

  const setTab = (newTab: TabType) => {
    setSearchParams({ tab: newTab })
  }

  const currentStats = tab === 'plsx' ? stats?.bots.plsx : stats?.bots.bridge
  const botAddress = currentStats?.address || ''
  const remaining = tab === 'plsx' ? remainingPlsx : remainingBridge
  const criteria = tab === 'bridge' ? BRIDGE_CRITERIA : PLSX_CRITERIA

  return (
    <>
      <Nav />
      <main className="flex-1 w-full max-w-3xl mx-auto pt-20 pb-8 px-4">
        {/* Tab toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setTab('bridge')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === 'bridge'
                ? 'bg-pulse-gradient text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Bridge Users
          </button>
          <button
            onClick={() => setTab('plsx')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === 'plsx'
                ? 'bg-pulse-gradient text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            PLSX Sacrificers
          </button>
        </div>

        {/* Main card — stats + sponsor + eligibility */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
          {/* Inline stats bar */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-5">
            <span className="text-slate-400">
              <span className={`font-semibold ${loading ? 'bg-slate-700 rounded animate-pulse text-transparent' : 'text-white'}`}>
                {loading ? '0,000' : commas(currentStats?.count ?? 0)}
              </span> Sponsored
            </span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-400">
              <span className={`font-semibold ${loading ? 'bg-slate-700 rounded animate-pulse text-transparent' : 'text-white'}`}>
                {loading ? '00,000' : commas(remaining ?? 0)}
              </span> Remaining
            </span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-400">
              <span className={`font-semibold ${loading ? 'bg-slate-700 rounded animate-pulse text-transparent' : 'text-white'}`}>
                {loading ? '$0.0000' : (costPerUserUsd ?? '–')}
              </span>/user
            </span>
          </div>

          {/* Sponsor panel */}
          <SponsorPanel botType={tab} botAddress={botAddress} />

          {/* Eligibility — compact line with tooltip */}
          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <div className="flex items-start gap-2 text-xs text-slate-500">
              <span className="flex-shrink-0 mt-px group relative cursor-help">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="hidden group-hover:block absolute bottom-6 left-0 z-10 w-56 p-3 bg-slate-900 border border-slate-700 rounded-lg shadow-xl text-xs text-slate-300">
                  <p className="font-medium text-white mb-2">Eligibility Criteria</p>
                  <ul className="space-y-1">
                    {criteria.map((c) => (
                      <li key={c} className="flex items-start gap-1.5">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </span>
              <span>
                Qualifies if: {criteria.join(' · ')}
              </span>
            </div>
          </div>
        </div>

        {/* PLSX claim gas */}
        {tab === 'plsx' && (
          <div className="mt-4">
            <ClaimGasFlow />
          </div>
        )}

        {/* Recent sponsorships */}
        <div className="mt-6">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Recent Sponsorships</h2>
          <SponsorTable botType={tab} showAmount={tab === 'bridge'} />
        </div>
      </main>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <Footer />
    </>
  )
}
